
import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Space, Tag, Tooltip, Modal, Form,
    Input, message, Typography, Row, Col, Statistic, Radio, Dropdown, Switch, Alert, Select, Empty
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
    GlobalOutlined, CheckCircleOutlined, ClockCircleOutlined, PauseCircleOutlined,
    PlayCircleOutlined, StopOutlined, SyncOutlined, SwapOutlined, MoreOutlined,
    ThunderboltOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import AdminTransferAction from '../../components/AdminTransferAction';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import dnsService from '../../services/dnsService';
import QosManageModal from '../../components/dns/QosManageModal';
import DnsDnssec from './DnsDnssec';
import { validateDomainRule } from '../../../shared/utils/validation';
import { dnsZoneStatusConfig, DNS_ZONE_STATUS_MAP } from '../../../shared/components/DnsZoneStatusTag';

import { adminPath } from '../../../shared/config/paths';

const { Title } = Typography;
const { confirm } = Modal;

// 后台统一配色：暂停=红，终止/删除=灰
const STATUS_COLOR_OVERRIDE = { suspended: 'error', terminated: 'default' };
const zoneStatus = (status) => {
    const c = dnsZoneStatusConfig(status);
    return { ...c, color: STATUS_COLOR_OVERRIDE[status] || c.color };
};
const fmtDate = (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-');

const DnsZones = () => {
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalZones: 0,
        activeZones: 0,
        totalRecords: 0
    });
    const [searchParams] = useSearchParams();
    const [sortInfo, setSortInfo] = useState({ field: 'created_at', order: 'desc' });
    const [searchValue, setSearchValue] = useState(() => searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState(undefined);
    const [pager, setPager] = useState({ page: 1, pageSize: 20, total: 0 });
    const [loadError, setLoadError] = useState('');
    const [actionModal, setActionModal] = useState({ visible: false, type: '', zone: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [actionForm] = Form.useForm();
    const [qosModalVisible, setQosModalVisible] = useState(false);
    const [selectedZoneForQos, setSelectedZoneForQos] = useState(null);
    const [dnssecZone, setDnssecZone] = useState(null);
    const [dnsProducts, setDnsProducts] = useState([]);

    const fetchDnsProducts = async () => {
        try {
            const result = await dnsService.getDnsProducts();
            if (result.success) {
                setDnsProducts(result.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch DNS products:', error);
        }
    };

    const showQosModal = (zone) => {
        setSelectedZoneForQos(zone);
        setQosModalVisible(true);
    };

    // 服务端分页 / 搜索 / 状态筛选；不传的参数沿用当前值
    const fetchZones = async (o = {}) => {
        const sort = o.sort || sortInfo;
        const search = o.search !== undefined ? o.search : searchValue;
        const status = o.status !== undefined ? o.status : statusFilter;
        const page = o.page || pager.page;
        const pageSize = o.pageSize || pager.pageSize;
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (sort.field) {
                params.sortField = sort.field;
                params.sortOrder = sort.order;
            }
            if (search) params.search = search;
            if (status) params.status = status;
            const result = await dnsService.getDnsZones(params);
            if (result.success) {
                setLoadError('');
                const data = Array.isArray(result.data) ? result.data : [];
                setZones(data);
                setPager({ page, pageSize, total: result.pagination?.total ?? data.length });
                const st = result.stats || {};
                setStats({
                    totalZones: st.total_zones ?? data.length,
                    activeZones: st.active_zones ?? 0,
                    totalRecords: st.total_records ?? 0,
                });
            } else {
                setLoadError(result.error || '获取DNS区域失败');
            }
        } catch (error) {
            setLoadError('获取DNS区域失败: ' + (error.error || error.message || ''));
        } finally {
            setLoading(false);
        }
    };

    // 排序 / 翻页都走服务端
    const handleTableChange = (pagination, filters, sorter, extra) => {
        if (extra && extra.action === 'sort') {
            let newSort = { field: '', order: '' };
            if (sorter && sorter.field && sorter.order) {
                newSort = { field: sorter.field, order: sorter.order === 'ascend' ? 'asc' : 'desc' };
            }
            setSortInfo(newSort);
            fetchZones({ sort: newSort, page: 1 });
            return;
        }
        fetchZones({ page: pagination.current, pageSize: pagination.pageSize });
    };

    useEffect(() => {
        fetchZones();
        fetchDnsProducts();
    }, []);

    const showCreateModal = () => {
        form.resetFields();
        setEditingZone(null);
        setIsModalVisible(true);
    };

    const showEditModal = (zone) => {
        setEditingZone(zone);
        form.setFieldsValue({
            zone: zone.zone,
            description: zone.description || '',
            status: zone.status,
            product_id: zone.product_id || null
        });
        setIsModalVisible(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingZone) {
                // 更新区域
                const result = await dnsService.updateDnsZone({
                    id: editingZone.id,
                    ...values
                });

                if (result.success) {
                    message.success('区域更新成功');
                    setIsModalVisible(false);
                    fetchZones();
                } else {
                    message.error(result.error || '更新DNS区域失败');
                }
            } else {
                // 创建新区域
                const payload = { ...values };
                // 未填写所属用户ID时不传该字段，避免传空字符串
                if (payload.user_id === '' || payload.user_id === undefined || payload.user_id === null) {
                    delete payload.user_id;
                }
                const result = await dnsService.createDnsZone(payload);

                if (result.success) {
                    message.success('区域创建成功');
                    setIsModalVisible(false);
                    fetchZones();
                } else {
                    message.error(result.error || '创建DNS区域失败');
                }
            }
        } catch (error) {
            // 表单验证错误或API调用失败
            if (error.errorFields) {
                // 这是表单验证错误
            } else {
                message.error('操作失败: ' + (error.error || error.message));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (zone) => {
        confirm({
            title: '确认删除',
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除区域 "${zone.display_zone || zone.zone}" 吗？此操作无法撤销，所有相关记录将被删除。`,
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await dnsService.deleteDnsZone(zone.id);

                    if (result.success) {
                        message.success('区域删除成功');
                        fetchZones();
                    } else {
                        message.error(result.error || '删除DNS区域失败');
                    }
                } catch (error) {
                    message.error('删除DNS区域失败: ' + (error.error || error.message));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleAction = (type, zone) => {
        if (type === 'qos') {
            showQosModal(zone);
            return;
        }
        if (type === 'dnssec') {
            setDnssecZone(zone);
            return;
        }
        if (type === 'mark_verified') {
            // 归属验证:管理员直接标记,不再按期限移除
            confirm({
                title: `标记 ${zone.display_zone || zone.zone} 归属已验证?`,
                onOk: async () => {
                    try {
                        await dnsService.markZoneVerified(zone.id);
                        message.success('已标记');
                        fetchZones();
                    } catch (error) {
                        message.error('操作失败: ' + (error.error || error.message || ''));
                    }
                },
            });
            return;
        }
        actionForm.resetFields();
        setActionModal({ visible: true, type, zone });
    };

    const executeAction = async () => {
        setActionLoading(true);
        try {
            const { type, zone } = actionModal;
            const values = await actionForm.validateFields().catch(() => ({}));
            let response;

            switch (type) {
                case 'suspend':
                    response = await dnsService.suspendZone(zone.id, values.reason || '');
                    break;
                case 'resume':
                    response = await dnsService.resumeZone(zone.id);
                    break;
                case 'terminate':
                    response = await dnsService.terminateZone(zone.id, values.hard_delete || false);
                    break;
                case 'activate':
                    response = await dnsService.activateZone(zone.id);
                    break;
                case 'change_owner':
                    response = await dnsService.changeZoneOwner(zone.id, values.new_user_id);
                    break;
                case 'force_sync':
                    response = await dnsService.forceSyncZone(zone.id);
                    break;
                default:
                    throw new Error('未知操作');
            }

            if (response.success) {
                message.success('操作成功');
                setActionModal({ visible: false, type: '', zone: null });
                fetchZones();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error(error.error || error.message || '操作失败');
        } finally {
            setActionLoading(false);
        }
    };

    const getActionItems = (record) => {
        const items = [];
        const status = record.status;

        if (status === 'active') {
            items.push({ key: 'suspend', label: '暂停', icon: <PauseCircleOutlined /> });
            items.push({ key: 'force_sync', label: '强制同步', icon: <SyncOutlined /> });
        }
        if (status === 'suspended') {
            items.push({ key: 'resume', label: '恢复', icon: <PlayCircleOutlined /> });
        }
        if (status === 'inactive') {
            items.push({ key: 'activate', label: '激活', icon: <CheckCircleOutlined /> });
        }
        if (record.verified === false) {
            items.push({ key: 'mark_verified', label: '标记已验证', icon: <CheckCircleOutlined /> });
        }
        items.push({ key: 'qos', label: 'QoS设置', icon: <ThunderboltOutlined /> });
        items.push({ key: 'dnssec', label: 'DNSSEC设置', icon: <SafetyCertificateOutlined /> });
        items.push({ key: 'terminate', label: '终止', icon: <StopOutlined />, danger: true });

        return items;
    };

    const getActionModalTitle = () => {
        const titles = {
            suspend: '暂停区域',
            resume: '恢复区域',
            terminate: '终止区域',
            activate: '激活区域',
            change_owner: '转移所有权',
            force_sync: '强制同步',
        };
        return titles[actionModal.type] || '操作';
    };

    const renderActionModalContent = () => {
        const { type, zone } = actionModal;

        switch (type) {
            case 'suspend':
                return (
                    <Form form={actionForm} layout="vertical">
                        <p>确定要暂停区域 {zone?.zone} 吗？</p>
                        <Form.Item name="reason" label="暂停原因">
                            <Input.TextArea rows={3} placeholder="请输入暂停原因（可选）" />
                        </Form.Item>
                    </Form>
                );
            case 'terminate':
                return (
                    <Form form={actionForm} layout="vertical">
                        <Alert message="警告" description="终止区域将停止所有DNS解析服务。" type="warning" showIcon style={{ marginBottom: 16 }} />
                        <p>确定要终止区域 {zone?.zone} 吗？</p>
                        <Form.Item name="hard_delete" valuePropName="checked" initialValue={false}>
                            <Switch checkedChildren="永久删除" unCheckedChildren="保留数据" />
                        </Form.Item>
                    </Form>
                );
            case 'change_owner':
                return (
                    <Form form={actionForm} layout="vertical">
                        <p>将区域 {zone?.zone} 转移给其他用户</p>
                        <Form.Item name="new_user_id" label="新用户ID" rules={[{ required: true, message: '请输入用户ID' }]}>
                            <Input type="number" placeholder="请输入新用户ID" />
                        </Form.Item>
                    </Form>
                );
            case 'force_sync':
                return <p>确定要强制同步区域 {zone?.zone} 到所有DNS节点吗？</p>;
            case 'resume':
            case 'activate':
                return <p>确定要{type === 'resume' ? '恢复' : '激活'}区域 {zone?.zone} 吗？</p>;
            default:
                return null;
        }
    };

    const columns = [
        {
            title: '区域',
            dataIndex: 'zone',
            key: 'zone',
            sorter: true,
            sortOrder: sortInfo.field === 'zone' ? (sortInfo.order === 'asc' ? 'ascend' : 'descend') : null,
            render: (text, record) => {
                // 优先显示 display_zone（Unicode IDN），否则显示 zone（Punycode）
                const displayName = record.display_zone || text;
                return (
                    <Tooltip title={text !== displayName ? `Punycode: ${text}` : null}>
                        <a onClick={() => navigate(adminPath(`/dns/records/${record.id}`), { state: { zoneName: displayName } })}>{displayName}</a>
                    </Tooltip>
                );
            },
        },
        {
            title: '用户',
            dataIndex: 'username',
            key: 'username',
            render: (username, record) => {
                if (!record.user_id) return '-';
                return (
                    <Tooltip title={record.user_email || ''}>
                        <a onClick={() => navigate(adminPath(`/users/${record.user_id}`))}>
                            {username || `UID:${record.user_id}`}
                        </a>
                    </Tooltip>
                );
            },
        },
        {
            title: '套餐',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (name) => name ? <Tag color="blue">{name}</Tag> : <Tag>无</Tag>,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status, r) => {
                const config = zoneStatus(status);
                return (
                    <Space size={4}>
                        <Tag color={config.color}>{config.text}</Tag>
                        {r.verified === false && (
                            <Tooltip title={r.verify_deadline ? `${String(r.verify_deadline).slice(0, 10)} 前 NS 未改将移除` : ''}><Tag color="orange">未验证</Tag></Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '记录数',
            dataIndex: 'records_count',
            key: 'records_count',
            render: (count) => count || 0
        },
        {
            title: 'QoS',
            dataIndex: 'has_qos',
            key: 'has_qos',
            render: (hasQos) => (
                <Tag color={hasQos ? 'blue' : 'default'}>
                    {hasQos ? '已启用' : '未启用'}
                </Tag>
            ),
        },
        {
            title: '版本',
            dataIndex: 'config_version',
            key: 'config_version',
            render: (version) => `v${version || 1}`
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: true,
            sortOrder: sortInfo.field === 'created_at' ? (sortInfo.order === 'asc' ? 'ascend' : 'descend') : null,
            render: fmtDate
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            sorter: true,
            sortOrder: sortInfo.field === 'updated_at' ? (sortInfo.order === 'asc' ? 'ascend' : 'descend') : null,
            render: (text, record) => fmtDate(record.updated_at || record.created_at)
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <AdminTransferAction type="dns" id={record.id} name={record.display_zone || record.zone} onDone={() => fetchZones()}>
                        <Tooltip title="转移给其他账号"><Button type="link" size="small" icon={<SwapOutlined />} /></Tooltip>
                    </AdminTransferAction>
                    <Tooltip title="管理记录">
                        <Button
                            type="primary"
                            icon={<GlobalOutlined />}
                            size="small"
                            onClick={() => navigate(adminPath(`/dns/records/${record.id}`), { state: { zoneName: record.display_zone || record.zone } })}
                        />
                    </Tooltip>
                    <Tooltip title="编辑">
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => showEditModal(record)}
                        />
                    </Tooltip>
                    <Dropdown
                        menu={{
                            items: getActionItems(record),
                            onClick: ({ key }) => handleAction(key, record),
                        }}
                        trigger={['click']}
                    >
                        <Button type="default" size="small" icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>DNS区域管理</Title>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card bordered={false}>
                        <Statistic
                            title="区域总数"
                            value={stats.totalZones}
                            prefix={<GlobalOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false}>
                        <Statistic
                            title="活跃区域"
                            value={stats.activeZones}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false}>
                        <Statistic
                            title="记录总数"
                            value={stats.totalRecords}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Card
                title="DNS区域列表"
                extra={
                    <Space>
                        <Select
                            allowClear
                            placeholder="状态"
                            style={{ width: 110 }}
                            value={statusFilter}
                            onChange={(v) => { setStatusFilter(v); fetchZones({ status: v || '', page: 1 }); }}
                            options={Object.keys(DNS_ZONE_STATUS_MAP).map((k) => ({ value: k, label: DNS_ZONE_STATUS_MAP[k].text }))}
                        />
                        <Input.Search
                            placeholder="区域 / 用户 / ID"
                            allowClear
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onSearch={(value) => {
                                setSearchValue(value);
                                fetchZones({ search: value, page: 1 });
                            }}
                            style={{ width: 200 }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={showCreateModal}
                        >
                            添加区域
                        </Button>
                    </Space>
                }
            >
                {loadError && (
                    <Alert type="error" showIcon style={{ marginBottom: 12 }} message={loadError}
                        action={<Button size="small" onClick={() => fetchZones()}>重试</Button>} />
                )}
                <Table
                    columns={columns}
                    dataSource={zones}
                    rowKey="id"
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    sticky
                    locale={{
                        emptyText: <Empty description={searchValue || statusFilter ? '无匹配区域' : '暂无DNS区域'} />
                    }}
                    pagination={{
                        current: pager.page,
                        pageSize: pager.pageSize,
                        total: pager.total,
                        pageSizeOptions: [20, 50, 100, 200],
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 个区域`,
                    }}
                    showSorterTooltip={false}
                />
            </Card>

            <Modal
                title={editingZone ? `编辑区域: ${editingZone.zone}` : "添加新区域"}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
                destroyOnClose={true}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        status: 'active',
                    }}
                >
                    <Form.Item
                        name="zone"
                        label="区域名称"
                        rules={[
                            { required: true, message: '请输入区域名称!' },
                            { validator: validateDomainRule }
                        ]}
                        disabled={!!editingZone}
                    >
                        <Input placeholder="example.com 或 中国.com" disabled={!!editingZone} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="描述 (可选)"
                    >
                        <Input.TextArea rows={2} placeholder="区域描述信息" />
                    </Form.Item>

                    {!editingZone && (
                        <Form.Item
                            name="user_id"
                            label="所属用户ID (可选)"
                            tooltip="留空则归属当前操作管理员；填写用户ID可代客户创建"
                        >
                            <Input type="number" placeholder="请输入用户ID (可选)" />
                        </Form.Item>
                    )}

                    {editingZone && (
                        <Form.Item
                            name="status"
                            label="状态"
                            rules={[{ required: true, message: '请选择状态!' }]}
                        >
                            <Radio.Group>
                                <Radio value="active">活跃</Radio>
                                <Radio value="inactive">未激活</Radio>
                            </Radio.Group>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="product_id"
                        label="套餐"
                    >
                        <Select
                            allowClear
                            placeholder="选择套餐 (可选)"
                            options={[
                                { value: null, label: '无套餐' },
                                ...dnsProducts.map(p => ({ value: p.id, label: p.name }))
                            ]}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 生命周期操作模态框 */}
            <Modal
                title={getActionModalTitle()}
                open={actionModal.visible}
                onOk={executeAction}
                onCancel={() => setActionModal({ visible: false, type: '', zone: null })}
                confirmLoading={actionLoading}
                okText="确定"
                cancelText="取消"
            >
                {renderActionModalContent()}
            </Modal>

            {/* QoS 管理模态框 */}
            <QosManageModal
                visible={qosModalVisible}
                onCancel={() => {
                    setQosModalVisible(false);
                    setSelectedZoneForQos(null);
                }}
                zoneId={selectedZoneForQos?.id}
                zoneName={selectedZoneForQos?.zone}
            />

            {/* DNSSEC 管理模态框 */}
            <DnsDnssec
                visible={!!dnssecZone}
                onClose={() => setDnssecZone(null)}
                zoneId={dnssecZone?.id}
                zoneName={dnssecZone?.display_zone || dnssecZone?.zone}
            />
        </div>
    );
};

export default DnsZones;
